import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Scale, Loader2 } from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type ComplianceStatus = 'compliant' | 'in_progress' | 'non_compliant';

interface ComplianceFramework {
  id: string;
  name: string;
  description: string;
  status: ComplianceStatus;
  requirements_total: number;
  requirements_met: number;
  evidence_count: number;
  updated_at: string;
}

interface ComplianceResponse {
  frameworks: ComplianceFramework[];
  stats: {
    total_frameworks: number;
    fully_compliant: number;
    requirements_met: number;
    requirements_total: number;
    evidence_items: number;
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function statusBadge(status: ComplianceStatus) {
  const base = 'px-2 py-0.5 rounded-full text-xs font-medium';
  switch (status) {
    case 'compliant':
      return <span className={`${base} bg-green-500/15 text-green-500`}>Compliant</span>;
    case 'in_progress':
      return <span className={`${base} bg-yellow-500/15 text-yellow-500`}>In Progress</span>;
    case 'non_compliant':
      return <span className={`${base} bg-red-500/15 text-red-500`}>Non-Compliant</span>;
  }
}

function progressColor(pct: number): string {
  if (pct >= 80) return 'bg-green-500';
  if (pct >= 50) return 'bg-yellow-500';
  return 'bg-red-500';
}

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

export function CompliancePage() {
  const [statusFilter, setStatusFilter] = useState<ComplianceStatus | 'all'>('all');

  const complianceQuery = useQuery<ComplianceResponse>({
    queryKey: ['governance', 'compliance'],
    queryFn: () => api<ComplianceResponse>('governance/evidence'),
  });

  const stats = complianceQuery.data?.stats;
  const frameworks = complianceQuery.data?.frameworks ?? [];
  const filtered = statusFilter === 'all' ? frameworks : frameworks.filter((f) => f.status === statusFilter);

  const overallPct = stats
    ? stats.requirements_total > 0
      ? Math.round((stats.requirements_met / stats.requirements_total) * 100)
      : 0
    : 0;

  return (
    <div className="p-8 max-w-6xl mx-auto space-y-8">
      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
          <Scale className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-foreground">Compliance Tracking</h1>
          <p className="text-muted-foreground mt-1">
            Monitor compliance frameworks, requirements, and evidence collection.
          </p>
        </div>
      </div>

      {/* Stats */}
      {complianceQuery.isLoading ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-20 rounded-lg bg-muted animate-pulse" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm text-muted-foreground">Frameworks</p>
            <p className="text-2xl font-bold text-foreground">{stats?.total_frameworks ?? 0}</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm text-muted-foreground">Fully Compliant</p>
            <p className="text-2xl font-bold text-green-500">{stats?.fully_compliant ?? 0}</p>
          </div>
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm text-muted-foreground">Requirements Met</p>
            <p className="text-2xl font-bold text-foreground">
              {stats?.requirements_met ?? 0}/{stats?.requirements_total ?? 0}
            </p>
          </div>
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm text-muted-foreground">Evidence Items</p>
            <p className="text-2xl font-bold text-foreground">{stats?.evidence_items ?? 0}</p>
          </div>
        </div>
      )}

      {/* Overall progress bar */}
      {complianceQuery.isSuccess && (
        <div className="rounded-lg border border-border bg-card p-6">
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-lg font-semibold text-foreground">Overall Compliance</h2>
            <span className="text-sm font-medium text-foreground">{overallPct}%</span>
          </div>
          <div className="h-3 rounded-full bg-muted overflow-hidden">
            <div
              className={`h-full rounded-full ${progressColor(overallPct)} transition-all`}
              style={{ width: `${overallPct}%` }}
            />
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            {stats?.requirements_met ?? 0} of {stats?.requirements_total ?? 0} requirements met across all frameworks.
          </p>
        </div>
      )}

      {/* Status filter */}
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">Filter:</span>
        {(['all', 'compliant', 'in_progress', 'non_compliant'] as const).map((opt) => {
          const labels: Record<string, string> = {
            all: 'All',
            compliant: 'Compliant',
            in_progress: 'In Progress',
            non_compliant: 'Non-Compliant',
          };
          return (
            <button
              key={opt}
              type="button"
              onClick={() => setStatusFilter(opt)}
              className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                statusFilter === opt
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground hover:bg-accent'
              }`}
            >
              {labels[opt]}
            </button>
          );
        })}
      </div>

      {/* Framework cards */}
      {complianceQuery.isLoading && (
        <div className="rounded-lg border border-border bg-card p-12 flex items-center justify-center gap-2">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          <span className="text-muted-foreground">Loading frameworks...</span>
        </div>
      )}

      {complianceQuery.isSuccess && filtered.length === 0 && (
        <div className="rounded-lg border border-border bg-card p-12 text-center text-muted-foreground">
          {statusFilter === 'all'
            ? 'No compliance frameworks configured yet.'
            : `No frameworks with status "${statusFilter.replace('_', ' ')}".`}
        </div>
      )}

      {complianceQuery.isSuccess && filtered.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((fw) => {
            const pct =
              fw.requirements_total > 0
                ? Math.round((fw.requirements_met / fw.requirements_total) * 100)
                : 0;
            return (
              <div key={fw.id} className="rounded-lg border border-border bg-card p-5 space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="font-medium text-foreground">{fw.name}</h3>
                  {statusBadge(fw.status)}
                </div>
                {fw.description && (
                  <p className="text-sm text-muted-foreground line-clamp-2">{fw.description}</p>
                )}
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-muted-foreground">Progress</span>
                    <span className="text-xs font-medium text-foreground">{pct}%</span>
                  </div>
                  <div className="h-2 rounded-full bg-muted overflow-hidden">
                    <div
                      className={`h-full rounded-full ${progressColor(pct)} transition-all`}
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span>
                    {fw.requirements_met}/{fw.requirements_total} requirements
                  </span>
                  <span>{fw.evidence_count} evidence items</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
